import type { ConvertHouseholdMemberToResidentInput, ConvertHouseholdMemberToResidentResult, ConvertResidentToHouseholdMemberInput, ConvertResidentToHouseholdMemberResult, CreateUnitHouseholdMemberInput, IUserRepository, PaginatedResponse, QueryParams } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE, TenantSelectionRequiredError } from '@yawi/domain';
import type {
  Ability,
  FiscalData,
  HouseholdMember,
  HouseholdMemberAccountStatus,
  ImpersonateUserResponse,
  MagicLinkVerifyResponse,
  TenantSelection,
  TenantWithBranding,
  User,
  UserUnit
} from '@yawi/types';
import { UserRole } from '@yawi/types';
import { ApiError } from '../../http/ApiError';
import { clearCurrentTenant, clearTokens, getApiClient, getRefreshToken, setCurrentTenant, setTokens } from '../../http/apiClient';

// Tipos de respuesta de la API
interface TokensDto {
  accessToken: string;
  refreshToken?: string; // Ausente en clientes web: viaja en cookie HttpOnly, no en el body.
  csrfToken?: string; // Solo presente en clientes web.
  expiresIn: number;
}

interface AbilityRuleDto {
  action: string;
  subject: string;
  conditions?: Record<string, unknown> | null;
  inverted?: boolean | null;
}

interface UpdateUserDto {
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
  tenantRole?: string;
  status?: 'ACTIVE' | 'BLOCKED';
  avatarUrl?: string;
  unitIds?: string[];
  acsCode?: string | null;
}

interface LoginResponse {
  user: ApiUser;
  // null cuando requiresTenantSelection es true — el correo resolvió a más
  // de una cuenta y todavía no se emiten tokens hasta elegir cuál.
  tokens: TokensDto | null;
  abilities?: AbilityRuleDto[];
  permissions?: AbilityRuleDto[];
  tenant?: {
    id: string;
    name: string;
    slug: string;
  } | null;
  requiresTenantSelection?: boolean;
  tenants?: TenantSelection[];
  /** Prueba de login (5 min) que exige POST /auth/select-tenant. */
  selectionToken?: string;
}

// A nivel de módulo (no de instancia): login y selectTenant pueden venir de instancias distintas.
let pendingSelectionToken: string | undefined;

interface ApiUser {
  id: string;
  memberId?: string | null; // Id del miembro del tenant (tenant_members.id)
  name: string;
  email: string;
  phone?: string | null | unknown; // El spec dice object | null, pero puede ser string
  role: string;
  tenantRole?: string;
  abilities?: AbilityRuleDto[];
  permissions?: AbilityRuleDto[];
  avatarUrl?: string | null | unknown; // El spec dice object | null, pero puede ser string
  status: 'ACTIVE' | 'BLOCKED' | 'DELETED';
  passwordSetByUser?: boolean;
  relatedTo?: { masterUserId: string; name: string } | null;
  residenceInfo?: {
    tower: string;
    unit: string;
    street?: string;
  };
  fiscalData?: {
    rfc: string;
    businessName: string;
    taxRegime: string;
    zipCode: string;
    address: string;
    email: string;
  };
  householdMembers?: Array<{
    id: string;
    name: string;
    relation: 'SPOUSE' | 'CHILD' | 'PARENT' | 'OTHER';
    age?: number;
    email?: string;
    phone?: string;
    accountStatus?: HouseholdMemberAccountStatus;
    editable?: boolean;
    isPrincipal?: boolean;
    is_principal?: boolean;
    unitId?: string;
  }>;
  units?: Array<{
    id: string;
    unitId?: string;
    unit_id?: string;
    isOwner?: boolean;
    isPrimary?: boolean;
    is_owner?: boolean;
    is_primary?: boolean;
    isPrincipal?: boolean;
    is_principal?: boolean;
    moveInDate?: string | null;
    moveOutDate?: string | null;
    move_in_date?: string | null;
    move_out_date?: string | null;
    unit: UserUnit['unit'];
  }>;
  unitIds?: string[];
  unit_ids?: string[];
  unitId?: string | null;
  unit_id?: string | null;
  isPrincipal?: boolean;
  is_principal?: boolean;
  unit?: UserUnit['unit'] | null;
  tenantMember?: {
    id?: string;
    unitId?: string | null;
    unit_id?: string | null;
    relationship?: string | null;
    unit?: UserUnit['unit'] | null;
  } | null;
  tenant_member?: {
    id?: string;
    unitId?: string | null;
    unit_id?: string | null;
    relationship?: string | null;
    unit?: UserUnit['unit'] | null;
  } | null;
  member?: {
    id?: string;
    unitId?: string | null;
    unit_id?: string | null;
    relationship?: string | null;
    unit?: UserUnit['unit'] | null;
  } | null;
  memberships?: Array<{
    id?: string;
    unitId?: string | null;
    unit_id?: string | null;
    unit?: UserUnit['unit'] | null;
  }>;
  unitAssignments?: Array<{
    id?: string;
    unitId?: string | null;
    unit_id?: string | null;
    unit?: UserUnit['unit'] | null;
  }>;
  unit_assignments?: Array<{
    id?: string;
    unitId?: string | null;
    unit_id?: string | null;
    unit?: UserUnit['unit'] | null;
  }>;
}

// Mapea el string de rol de la API al enum UserRole
const mapRole = (role: string | undefined | null): UserRole | string => {
  if (!role) return UserRole.RESIDENT;
  switch (role.toUpperCase()) {
    case 'ADMIN':
    case 'TENANT_ADMIN': // El backend usa TENANT_ADMIN para admins de tenant
      return UserRole.ADMIN;
    // Sin este caso caía en `default: RESIDENT`, así que un admin de sección
    // se mostraba como residente y desaparecía de los listados de personal.
    case 'SECTION_ADMIN':
      return UserRole.SECTION_ADMIN;
    case 'GUARD': return UserRole.GUARD;
    case 'FAMILIAR': return 'FAMILIAR';
    case 'RESIDENT':
    default: return UserRole.RESIDENT;
  }
};

// Helper para convertir valores que pueden ser object | null a string | undefined
const safeStringValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value;
  return String(value);
};

const hasUnitShape = (value: unknown): value is UserUnit['unit'] => (
  value !== null
  && value !== undefined
  && typeof value === 'object'
  && 'id' in value
  && ('name' in value || 'type' in value || 'number' in value || 'exteriorNumber' in value)
);

const makeFallbackUnit = (unitId: string): UserUnit['unit'] => ({
  id: unitId,
  type: 'HOUSE' as UserUnit['unit']['type'],
  name: `Unidad asignada (${unitId.slice(0, 8)})`,
});

const mapApiUserUnits = (apiUser: ApiUser): UserUnit[] | undefined => {
  if (apiUser.units?.length) {
    return apiUser.units
      .map((assignment): UserUnit | null => {
        const unit = assignment.unit || (hasUnitShape(assignment) ? assignment : undefined);
        const unitId = assignment.unitId || assignment.unit_id || unit?.id || assignment.id;
        if (!unitId) return null;

        return {
          id: assignment.id || `${apiUser.id}-${unitId}`,
          unitId,
          isOwner: assignment.isOwner ?? assignment.is_owner ?? true,
          isPrimary: assignment.isPrimary ?? assignment.is_primary ?? true,
          isPrincipal: assignment.isPrincipal ?? assignment.is_principal,
          moveInDate: assignment.moveInDate ?? assignment.move_in_date,
          moveOutDate: assignment.moveOutDate ?? assignment.move_out_date,
          unit: unit || makeFallbackUnit(unitId),
        };
      })
      .filter((assignment): assignment is UserUnit => Boolean(assignment));
  }

  const tenantMember = apiUser.tenantMember || apiUser.tenant_member || apiUser.member;
  const membership = apiUser.memberships?.[0] || apiUser.unitAssignments?.[0] || apiUser.unit_assignments?.[0];
  const unit = tenantMember?.unit || apiUser.unit;
  const unitId = tenantMember?.unitId
    || tenantMember?.unit_id
    || membership?.unitId
    || membership?.unit_id
    || apiUser.unitId
    || apiUser.unit_id
    || apiUser.unitIds?.[0]
    || apiUser.unit_ids?.[0]
    || unit?.id;

  if (!unitId) return undefined;

  return [{
    id: tenantMember?.id || membership?.id || `${apiUser.id}-${unitId}`,
    unitId,
    isOwner: true,
    isPrimary: true,
    // Con una sola unidad resuelta, el flag general del usuario no es ambiguo.
    isPrincipal: apiUser.isPrincipal ?? apiUser.is_principal,
    unit: unit || membership?.unit || makeFallbackUnit(unitId),
  }];
};

const normalizeAbilities = (abilities?: AbilityRuleDto[]): AbilityRuleDto[] | undefined => {
  if (!abilities?.length) return undefined;

  const subjectAliases: Record<string, string> = {
    accesscontrols: 'AccessControl',
    accesscontrol: 'AccessControl',
    accesslogs: 'AccessLog',
    accesslog: 'AccessLog',
    accessdevices: 'AccessDevice',
    accessdevice: 'AccessDevice',
    amenities: 'Amenity',
    amenity: 'Amenity',
    announcements: 'Announcement',
    announcement: 'Announcement',
    charges: 'Charge',
    charge: 'Charge',
    documents: 'Document',
    document: 'Document',
    expenses: 'Expense',
    expense: 'Expense',
    householdmembers: 'HouseholdMember',
    householdmember: 'HouseholdMember',
    payments: 'Payment',
    payment: 'Payment',
    polls: 'Poll',
    poll: 'Poll',
    residentmenu: 'ResidentMenu',
    residents: 'ResidentMenu',
    resident: 'ResidentMenu',
    roles: 'Role',
    role: 'Role',
    rolemenu: 'RoleMenu',
    settings: 'Settings',
    tickets: 'Ticket',
    ticket: 'Ticket',
    units: 'Unit',
    unit: 'Unit',
    users: 'User',
    user: 'User',
    staffmenu: 'StaffMenu',
    staff: 'StaffMenu',
    personal: 'StaffMenu',
    vehicles: 'Vehicle',
    vehicle: 'Vehicle',
  };

  return abilities.map((ability) => ({
    action: String(ability.action).toLowerCase() as Ability['action'],
    subject: (subjectAliases[String(ability.subject).replace(/[^a-z0-9]/gi, '').toLowerCase()] ?? ability.subject) as Ability['subject'],
    conditions: ability.conditions ?? undefined,
    inverted: ability.inverted ?? undefined,
  }));
};

// Mapea la respuesta de la API al tipo User interno
const mapApiUserToUser = (apiUser: ApiUser, abilities?: AbilityRuleDto[]): User => {
  const mappedAbilities = normalizeAbilities(abilities ?? apiUser.abilities ?? apiUser.permissions);
  const mappedUnits = mapApiUserUnits(apiUser);
  // Algunos endpoints (ej. /auth/login) no traen isPrincipal a nivel raíz del
  // usuario, solo dentro de units[]. Con una sola unidad no hay ambigüedad
  // posible, así que usamos ese valor como respaldo.
  const isPrincipal = apiUser.isPrincipal
    ?? apiUser.is_principal
    ?? (mappedUnits?.length === 1 ? mappedUnits[0].isPrincipal ?? false : false);

  return {
    ...apiUser,
    phone: safeStringValue(apiUser.phone),
    avatarUrl: safeStringValue(apiUser.avatarUrl),
    role: mapRole(apiUser.role),
    tenantRole: apiUser.tenantRole as User['tenantRole'],
    abilities: mappedAbilities,
    pin: '', // La API no devuelve el PIN por seguridad
    isPrincipal,
    units: mappedUnits,
    householdMembers: apiUser.householdMembers?.map((member) => ({
      id: member.id,
      name: member.name,
      relation: member.relation,
      email: member.email || '',
      age: member.age,
      phone: member.phone,
      accountStatus: member.accountStatus || 'PENDING_VERIFICATION',
      editable: member.editable ?? true,
      isPrincipal: member.isPrincipal ?? member.is_principal ?? false,
      unitId: member.unitId,
    })),
  };
};

export class ApiUserRepository implements IUserRepository {
  async getCurrentUser(): Promise<User> {
    const client = getApiClient();
    // El endpoint /me devuelve { user, tokens, tenant, ... }
    const response = await client.get<{ user: ApiUser; abilities?: AbilityRuleDto[]; permissions?: AbilityRuleDto[] }>('v1/users/me');
    return mapApiUserToUser(response.user, response.abilities ?? response.permissions);
  }

  async login(email: string, pin: string, tenantId?: string): Promise<User> {
    const client = getApiClient();

    const response = await client.post<LoginResponse>('v1/auth/login', {
      email,
      password: pin, // La API usa password, pero la interfaz usa pin
      ...(tenantId ? { tenantId } : {}),
    });

    // El mismo correo puede resolver a más de una cuenta en tenants distintos:
    // sin tokens todavía, el caller debe capturar TenantSelectionRequiredError
    // y llamar selectTenant(userId, tenantId) con la elegida.
    pendingSelectionToken = response.selectionToken;
    if (response.requiresTenantSelection) {
      throw new TenantSelectionRequiredError(response.user.id, response.tenants ?? []);
    }

    if (!response.tokens) {
      throw new Error('El servidor no devolvió una sesión válida.');
    }

    // Guardar tokens
    await setTokens(response.tokens.accessToken, response.tokens.refreshToken, response.tokens.csrfToken);

    // Guardar tenant si viene en la respuesta
    if (response.tenant) {
      await setCurrentTenant(response.tenant);
    }

    return mapApiUserToUser(response.user, response.abilities ?? response.permissions);
  }

  async updatePin(userId: string, newPin: string): Promise<void> {
    const client = getApiClient();
    // La API usa change-password, que requiere currentPassword
    // Por ahora, intentamos el endpoint de pin, si no existe, usamos change-password
    try {
      await client.patch(`v1/users/${userId}/pin`, { pin: newPin });
    } catch {
      // Si falla, intentar con change-password (requiere currentPassword que no tenemos)
      // Por ahora, lanzamos error indicando que se necesita la contraseña actual
      throw new Error('To change password, use change-password endpoint with current password');
    }
  }

  async getAllUsers(): Promise<User[]> {
    const client = getApiClient();
    // La API puede devolver paginado o directamente el array
    const response = await client.get<{ data?: ApiUser[]; items?: ApiUser[] } | ApiUser[]>('v1/users');

    let users: ApiUser[];
    if (Array.isArray(response)) {
      users = response;
    } else if (response.data) {
      users = response.data;
    } else if (response.items) {
      users = response.items;
    } else {
      users = [];
    }

    return users.map((user) => mapApiUserToUser(user));
  }

  async getUsers(params?: QueryParams): Promise<PaginatedResponse<User>> {
    const client = getApiClient();
    // El backend filtra (texto, estatus, rol, principalidad) -> cuenta -> ordena
    // -> pagina, y devuelve `meta` con el total ya filtrado. Filtrar o paginar
    // aquí rompe el orden: el total deja de cuadrar con el contenido de la página
    // y las páginas quedan mal numeradas.
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiUser>>(
      `v1/users?${queryString}`
    );
    return {
      ...response,
      data: response.data.map((user) => mapApiUserToUser(user)),
    };
  }

  async getUserById(id: string): Promise<User | null> {
    const client = getApiClient();
    try {
      const apiUser = await client.get<ApiUser>(`v1/users/${id}`);
      return mapApiUserToUser(apiUser);
    } catch {
      return null;
    }
  }

  async createUser(user: Omit<User, 'id'>): Promise<User> {
    const client = getApiClient();
    const {
      abilities: _abilities,
      permissions: _permissions,
      ...payload
    } = user as Omit<User, 'id'> & { permissions?: unknown };
    const apiUser = await client.post<ApiUser>('v1/users', payload);
    return mapApiUserToUser(apiUser);
  }

  async updateUser(user: User): Promise<User> {
    const client = getApiClient();
    const dto: UpdateUserDto = {};
    const raw = user as unknown as Record<string, unknown>;
    if (user.name !== undefined) dto.name = user.name;
    if (user.email !== undefined) dto.email = user.email;
    if (user.phone !== undefined) dto.phone = user.phone;
    if (user.role !== undefined) dto.role = typeof user.role === 'string' ? user.role : String(user.role);
    if (raw.tenantRole !== undefined) dto.tenantRole = String(raw.tenantRole);
    // DELETED y PENDING_VERIFICATION son estados que controla el backend
    // (soft-delete e invitación sin aceptar) — no se pueden setear vía PUT genérico.
    if (user.status !== undefined && user.status !== 'DELETED' && user.status !== 'PENDING_VERIFICATION') {
      dto.status = user.status;
    }
    if (user.avatarUrl !== undefined) dto.avatarUrl = user.avatarUrl;
    if (raw.unitIds !== undefined) dto.unitIds = raw.unitIds as string[];
    if (raw.acsCode !== undefined) dto.acsCode = raw.acsCode === '' ? null : (raw.acsCode as string);
    const apiUser = await client.put<ApiUser>(`v1/users/${user.id}`, dto);
    return mapApiUserToUser(apiUser);
  }

  async deleteUser(userId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/users/${userId}`);
  }

  async restoreUser(userId: string): Promise<User> {
    const client = getApiClient();
    const apiUser = await client.post<ApiUser>(`v1/users/${userId}/restore`);
    return mapApiUserToUser(apiUser);
  }

  // Método adicional para logout
  async logout(): Promise<void> {
    try {
      const client = getApiClient();
      await client.post('v1/auth/logout');
    } finally {
      await clearTokens();
      await clearCurrentTenant();
    }
  }

  // Métodos adicionales para perfil
  async updateProfile(updates: Partial<User>): Promise<User> {
    const client = getApiClient();
    const response = await client.put<ApiUser | { user: ApiUser }>('v1/users/me/profile', updates);
    const apiUser = 'user' in response ? response.user : response;
    return mapApiUserToUser(apiUser);
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const client = getApiClient();
    await client.post('v1/users/me/change-password', {
      currentPassword,
      newPassword,
    });
  }

  // Métodos para household members
  // Normalizes any household-member payload (list item, create/update response)
  // into a complete domain object. The create/update endpoints can return a
  // partial shape; `fallback` fills the gaps so the UI never receives a member
  // missing id/name/relation/status.
  private normalizeMember(
    raw:
      | (Omit<Partial<HouseholdMember>, 'unitId'> & {
          is_principal?: boolean;
          isPrincipal?: boolean;
          unitId?: string | null;
          unit_id?: string | null;
        })
      | null
      | undefined,
    fallback: Partial<HouseholdMember> = {},
  ): HouseholdMember {
    const src = raw ?? {};
    return {
      id: src.id ?? fallback.id ?? `temp-${Date.now()}`,
      name: src.name ?? fallback.name ?? '',
      relation: src.relation ?? fallback.relation ?? 'OTHER',
      email: src.email ?? fallback.email ?? '',
      age: src.age ?? fallback.age,
      phone: src.phone ?? fallback.phone,
      accountStatus: src.accountStatus ?? fallback.accountStatus ?? 'PENDING_VERIFICATION',
      editable: src.editable ?? fallback.editable ?? true,
      isPrincipal: src.isPrincipal ?? src.is_principal ?? fallback.isPrincipal ?? false,
      unitId: src.unitId ?? src.unit_id ?? fallback.unitId ?? undefined,
      createdAt: src.createdAt,
      updatedAt: src.updatedAt,
    };
  }

  async getHouseholdMembers(): Promise<HouseholdMember[]> {
    const client = getApiClient();
    const members = await client.get<ApiUser['householdMembers']>('v1/users/me/household-members');
    return (members || []).map((member) => this.normalizeMember(member));
  }

  async createHouseholdMember(member: Omit<HouseholdMember, 'id' | 'accountStatus'>): Promise<HouseholdMember> {
    const client = getApiClient();
    const apiMember = await client.post<Partial<HouseholdMember>>('v1/users/me/household-members', member);
    // Backend may return the created member, a wrapper, or nothing useful —
    // normalize and fall back to the submitted values so it always renders.
    return this.normalizeMember(apiMember, member);
  }

  async updateHouseholdMember(memberId: string, member: Partial<HouseholdMember>): Promise<HouseholdMember> {
    const client = getApiClient();
    const apiMember = await client.put<Partial<HouseholdMember>>(`v1/users/me/household-members/${memberId}`, member);
    return this.normalizeMember(apiMember, { ...member, id: memberId });
  }

  async deleteHouseholdMember(memberId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/users/me/household-members/${memberId}`);
  }

  async getUserHouseholdMembers(userId: string): Promise<HouseholdMember[]> {
    void userId;
    return [];
  }

  async createUserHouseholdMember(
    userId: string,
    member: Omit<HouseholdMember, 'id' | 'accountStatus'>,
  ): Promise<HouseholdMember> {
    void userId;
    void member;
    throw new Error('El backend actual solo permite crear miembros del hogar para el usuario autenticado: /v1/users/me/household-members.');
  }

  async getUnitHouseholdMembers(unitId: string): Promise<HouseholdMember[]> {
    const client = getApiClient();
    const members = await client.get<ApiUser['householdMembers']>(`v1/units/${unitId}/household-members`);
    return (members || []).map((member) => this.normalizeMember(member));
  }

  async createUnitHouseholdMember(
    unitId: string,
    residentId: string,
    member: CreateUnitHouseholdMemberInput,
  ): Promise<HouseholdMember> {
    const client = getApiClient();
    const apiMember = await client.post<Partial<HouseholdMember>>(
      `v1/units/${unitId}/residents/${residentId}/household-members`,
      member,
    );
    return this.normalizeMember(apiMember, member);
  }

  async convertResidentToHouseholdMember(
    unitId: string,
    residentId: string,
    input: ConvertResidentToHouseholdMemberInput,
  ): Promise<ConvertResidentToHouseholdMemberResult> {
    const client = getApiClient();
    let response: {
      user?: ApiUser;
      householdMember?: Partial<HouseholdMember>;
      member?: Partial<HouseholdMember>;
    } | ApiUser;

    try {
      response = await client.post<{
        user?: ApiUser;
        householdMember?: Partial<HouseholdMember>;
        member?: Partial<HouseholdMember>;
      } | ApiUser>(
        `v1/units/${unitId}/residents/${residentId}/convert-to-household-member`,
        {
          assignToResidentId: input.assignToResidentId,
          relation: input.relation,
          ...(input.age !== undefined ? { age: input.age } : {}),
        },
      );
    } catch (error) {
      if (ApiError.isApiError(error) && error.statusCode === 404) {
        throw new Error('El backend aun no expone la conversion administrativa de residente a miembro del hogar. Falta POST /v1/units/:unitId/residents/:residentId/convert-to-household-member.');
      }
      throw error;
    }

    const apiUser = 'user' in response ? response.user : response;
    const rawMember = 'householdMember' in response
      ? response.householdMember
      : 'member' in response
        ? response.member
        : undefined;
    const user = mapApiUserToUser(apiUser as ApiUser);

    return {
      user,
      householdMember: this.normalizeMember(rawMember, {
        id: residentId,
        name: user.name,
        email: user.email,
        phone: user.phone,
        relation: input.relation,
        accountStatus: 'ACTIVE',
      }),
    };
  }

  // Métodos para fiscal data
  async convertHouseholdMemberToResident(
    unitId: string,
    memberId: string,
    input: ConvertHouseholdMemberToResidentInput = {},
  ): Promise<ConvertHouseholdMemberToResidentResult> {
    const client = getApiClient();
    const response = await client.post<{ user?: ApiUser } | ApiUser>(
      `v1/units/${unitId}/household-members/${memberId}/convert-to-resident`,
      input,
    );
    const apiUser = 'user' in response ? response.user : response;

    return {
      user: mapApiUserToUser(apiUser as ApiUser),
    };
  }

  async getFiscalData(): Promise<FiscalData | null> {
    const client = getApiClient();
    try {
      const fiscalData = await client.get<FiscalData>('v1/users/me/fiscal-data');
      return fiscalData;
    } catch {
      return null;
    }
  }

  async upsertFiscalData(fiscalData: FiscalData): Promise<FiscalData> {
    const client = getApiClient();
    const { rfc, businessName, taxRegime, zipCode, address, email } = fiscalData;
    const payload = { rfc, businessName, taxRegime, zipCode, address, email };
    return await client.put<FiscalData>('v1/users/me/fiscal-data', payload);
  }

  async deleteFiscalData(): Promise<void> {
    const client = getApiClient();
    await client.delete('v1/users/me/fiscal-data');
  }

  // Métodos administrativos
  async blockUser(userId: string): Promise<User> {
    const client = getApiClient();
    const apiUser = await client.patch<ApiUser>(`v1/users/${userId}/block`);
    return mapApiUserToUser(apiUser);
  }

  async unblockUser(userId: string): Promise<User> {
    const client = getApiClient();
    const apiUser = await client.patch<ApiUser>(`v1/users/${userId}/unblock`);
    return mapApiUserToUser(apiUser);
  }

  async impersonateMember(memberId: string): Promise<ImpersonateUserResponse> {
    const client = getApiClient();
    // Tokens are NOT saved here - the caller (AuthContext) decides what to do with them.
    return client.post<ImpersonateUserResponse>(`v1/users/${memberId}/impersonate`);
  }

  // Método para refresh token. En mobile manda el refreshToken guardado en
  // SecureStore; en web no manda body — el backend lee el refresh token de
  // la cookie HttpOnly (HttpClient ya agrega credentials:'include' y el
  // header x-csrf-token automáticamente).
  async refreshToken(): Promise<void> {
    const refreshTokenValue = getRefreshToken();

    const client = getApiClient();
    const response = await client.post<TokensDto>(
      '/v1/auth/refresh',
      refreshTokenValue ? { refreshToken: refreshTokenValue } : undefined
    );

    await setTokens(response.accessToken, response.refreshToken, response.csrfToken);
  }

  // Multi-tenant methods
  async getMyTenants(): Promise<TenantSelection[]> {
    const client = getApiClient();
    return client.get<TenantSelection[]>('v1/auth/my-tenants');
  }

  async getMyAccounts(): Promise<TenantSelection[]> {
    const client = getApiClient();
    return client.get<TenantSelection[]>('v1/auth/my-accounts');
  }

  async selectTenant(userId: string, tenantId: string): Promise<User> {
    const client = getApiClient();
    // El endpoint es público (no hay JWT durante el login con selección pendiente),
    // por lo que userId debe viajar explícito en el body.
    const response = await client.post<LoginResponse>('v1/auth/select-tenant', {
      userId,
      tenantId,
      selectionToken: pendingSelectionToken,
    });
    pendingSelectionToken = undefined;

    if (!response.tokens) {
      throw new Error('El servidor no devolvió una sesión válida.');
    }
    await setTokens(response.tokens.accessToken, response.tokens.refreshToken, response.tokens.csrfToken);

    if (response.tenant) {
      await setCurrentTenant(response.tenant);
    }

    return mapApiUserToUser(response.user);
  }

  // Magic Link methods
  async verifyMagicLink(token: string): Promise<MagicLinkVerifyResponse> {
    const client = getApiClient();
    const response = await client.post<{
      user: ApiUser;
      tokens: TokensDto | null;
      tenant: TenantWithBranding | null;
      requiresTenantSelection: boolean;
      tenants?: TenantSelection[];
      selectionToken?: string;
      abilities?: AbilityRuleDto[];
      permissions?: AbilityRuleDto[];
    }>('v1/auth/magic-link/verify', { token });
    pendingSelectionToken = response.selectionToken;
    const abilities = response.abilities ?? response.permissions ?? [];

    // Si hay tokens, guardarlos
    if (response.tokens) {
      await setTokens(response.tokens.accessToken, response.tokens.refreshToken, response.tokens.csrfToken);
    }

    return {
      user: mapApiUserToUser(response.user, abilities),
      tokens: response.tokens ? {
        accessToken: response.tokens.accessToken,
        refreshToken: response.tokens.refreshToken,
        csrfToken: response.tokens.csrfToken,
        expiresIn: response.tokens.expiresIn,
      } : null,
      tenant: response.tenant,
      requiresTenantSelection: response.requiresTenantSelection,
      tenants: response.tenants,
      abilities,
    };
  }

  async requestMagicLink(email: string): Promise<void> {
    const client = getApiClient();
    // La API siempre devuelve 204 por seguridad (no revela si el email existe)
    await client.post('v1/auth/magic-link/request', { email });
  }

  async selectTenantForMagicLink(userId: string, tenantId: string): Promise<MagicLinkVerifyResponse> {
    const client = getApiClient();
    const response = await client.post<{
      user: ApiUser;
      tokens: TokensDto;
      tenant: TenantWithBranding;
      requiresTenantSelection: boolean;
      abilities?: AbilityRuleDto[];
      permissions?: AbilityRuleDto[];
    }>('v1/auth/select-tenant', { userId, tenantId, selectionToken: pendingSelectionToken });
    pendingSelectionToken = undefined;
    const abilities = response.abilities ?? response.permissions ?? [];

    // Guardar tokens
    await setTokens(response.tokens.accessToken, response.tokens.refreshToken, response.tokens.csrfToken);

    // Sin esto, las siguientes requests no llevan x-tenant-id y el backend
    // no sabe a cuál cuenta/tenant del usuario corresponde la sesión.
    if (response.tenant) {
      await setCurrentTenant(response.tenant);
    }

    return {
      user: mapApiUserToUser(response.user, abilities),
      tokens: {
        accessToken: response.tokens.accessToken,
        refreshToken: response.tokens.refreshToken,
        csrfToken: response.tokens.csrfToken,
        expiresIn: response.tokens.expiresIn,
      },
      tenant: response.tenant,
      requiresTenantSelection: false,
      abilities,
    };
  }

  // Resend household member invitation
  async resendHouseholdMemberInvitation(memberId: string): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/users/me/household-members/${memberId}/resend-invitation`);
  }

  async resendUnitHouseholdMemberInvitation(unitId: string, memberId: string): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/units/${unitId}/household-members/${memberId}/resend-invitation`);
  }

  async transferUnitPrincipal(unitId: string, targetMemberId: string): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/units/${unitId}/principal`, { targetMemberId });
  }

  async deleteUnitHouseholdMember(unitId: string, memberId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/units/${unitId}/household-members/${memberId}`);
  }

  // Password recovery
  async forgotPassword(email: string): Promise<void> {
    const client = getApiClient();
    await client.post('v1/auth/forgot-password', { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    const client = getApiClient();
    await client.post('v1/auth/reset-password', { token, newPassword });
  }

  async setInitialPassword(newPassword: string): Promise<void> {
    const client = getApiClient();
    await client.post('v1/auth/set-initial-password', { newPassword });
  }
}
